// CRITICAL: Bootstrap MUST be imported first to ensure React loads before any other code
import './bootstrap';

import { createRoot } from 'react-dom/client';
import App from './App';

import { Provider } from 'react-redux';
import { store, persistor } from 'store';
import { PersistGate } from 'redux-persist/integration/react';
import { queryClient } from 'core/query/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FeatureFlagProvider } from 'containers/FeatureFlagProvider';
import '@szhsin/react-menu/dist/index.css';

class ReduxHelper {
  getDispatch() {
    return store.dispatch;
  }
  getState() {
    return store.getState();
  }
}

/** This is only used when the `dispatch` function from Redux is not accessible
 * Always use `dispatch` provided by useDispatch
 *
 * Reason: Accessing "store" in another file on app mount errors out
 */
const reduxHelper = new ReduxHelper();

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);

// Optional API mocking for local dev (VITE_MOCK=true pnpm dev) — lets you click
// through the UI with no backend, auth, or CORS. See docs/local-development.md.
async function enableMocking() {
  // DEV gate also lets Vite drop the mocks chunk from production builds.
  if (!import.meta.env.DEV || import.meta.env.VITE_MOCK !== 'true') return;
  const { worker } = await import('./mocks/browser');
  await worker.start({ onUnhandledFrame: 'bypass' });
}

enableMocking()
  .catch((error) => {
    console.error('Failed to start API mocking, rendering without it:', error);
  })
  .finally(() => {
    root.render(
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            <FeatureFlagProvider>
              <App />
              {/* Temporarily disabled - causes hover background effects */}
              {/* <ReactQueryDevtools position="bottom-left" initialIsOpen={false} /> */}
            </FeatureFlagProvider>
          </QueryClientProvider>
        </PersistGate>
      </Provider>,
    );
  });

export { reduxHelper };
